import React, { useEffect, useMemo, useState } from "react";
import "./ListesPermanentes.css";

/**
 * Composant de listes permanentes indépendantes du planning.
 * Prévu pour : liste de courses, films à voir, idées album, choses à faire, etc.
 *
 * Props conseillées :
 * - idProprio : identifiant utilisateur connecté
 * - apiBaseUrl : dossier PHP, exemple "/php"
 * - onClose : optionnel, pour fermer le panneau/modal
 */
export default function ListesPermanentes({ apiBaseUrl = "/go/php", onClose }) {
  const [listes, setListes] = useState([]);
  const [listeActiveId, setListeActiveId] = useState(null);
  const [nouvelleListe, setNouvelleListe] = useState("");
  const [nouvelItem, setNouvelItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const listeActive = useMemo(
    () => listes.find((liste) => liste.id_liste === listeActiveId) || listes[0] || null,
    [listes, listeActiveId]
  );

  useEffect(() => {
    chargerListes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function api(action, payload = {}) {
    const response = await fetch(`${apiBaseUrl}/listes/listes-permanentes.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...payload }),
    });

    const data = await response.json();
    if (!data.success) throw new Error(data.error || "Erreur serveur");
    return data;
  }

  async function chargerListes(activeIdConserve = listeActiveId) {
    setLoading(true);
    setMessage("");

    try {
      const data = await api("getListes");
      const nouvellesListes = data.listes || [];

      setListes(nouvellesListes);

      if (activeIdConserve && nouvellesListes.some((liste) => liste.id_liste === activeIdConserve)) {
        setListeActiveId(activeIdConserve);
      } else if (nouvellesListes.length) {
        setListeActiveId(nouvellesListes[0].id_liste);
      } else {
        setListeActiveId(null);
      }
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function ajouterListe(event) {
    event.preventDefault();
    const titre = nouvelleListe.trim();
    if (!titre) return;

    try {
      const data = await api("addListe", { titre_liste: titre });
      setNouvelleListe("");
      await chargerListes(data.id_liste);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function supprimerListe(idListe) {
    if (!window.confirm("Supprimer cette liste et tous ses éléments ?")) return;

    try {
      await api("deleteListe", { id_liste: idListe });
      await chargerListes(null);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function deplacerListe(idListe, direction) {
    const indexActuel = listes.findIndex((liste) => liste.id_liste === idListe);
    const indexCible = direction === "up" ? indexActuel - 1 : indexActuel + 1;

    if (indexActuel < 0 || indexCible < 0 || indexCible >= listes.length) return;

    const listesReordonnees = [...listes];
    [listesReordonnees[indexActuel], listesReordonnees[indexCible]] = [
      listesReordonnees[indexCible],
      listesReordonnees[indexActuel],
    ];

    setListes(listesReordonnees);
    setListeActiveId(idListe);

    try {
      await api("reorderListes", {
        ordre: listesReordonnees.map((liste, index) => ({
          id_liste: liste.id_liste,
          ordre_affichage: index + 1,
        })),
      });
      await chargerListes(idListe);
    } catch (error) {
      setMessage(error.message);
      await chargerListes(idListe);
    }
  }

  async function ajouterItem(event) {
    event.preventDefault();
    const texte = nouvelItem.trim();
    if (!texte || !listeActive) return;

    try {
      await api("addItem", { id_liste: listeActive.id_liste, texte_item: texte });
      setNouvelItem("");
      await chargerListes(listeActive.id_liste);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function toggleItem(item) {
    try {
      await api("toggleItem", {
        id_item: item.id_item,
        item_coche: item.item_coche ? 0 : 1,
      });
      await chargerListes(listeActive?.id_liste);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function supprimerItem(idItem) {
    try {
      await api("deleteItem", { id_item: idItem });
      await chargerListes(listeActive?.id_liste);
    } catch (error) {
      setMessage(error.message);
    }
  }

  return (
    <section className="lp-panel" aria-label="Listes permanentes">
      <header className="lp-header">
        <div>
          <h2>Listes permanentes</h2>
          <p>Courses, films à voir, idées, préparatifs… hors planning.</p>
        </div>
        {onClose && (
          <button className="lp-close" onClick={onClose} title="Fermer">×</button>
        )}
      </header>

      {message && <div className="lp-message">{message}</div>}
      {loading && <div className="lp-loading">Chargement…</div>}

      <div className="lp-layout">
        <aside className="lp-sidebar">
          <form className="lp-add-list" onSubmit={ajouterListe}>
            <input
              value={nouvelleListe}
              onChange={(e) => setNouvelleListe(e.target.value)}
              placeholder="Nouvelle liste"
            />
            <button type="submit">+</button>
          </form>

          <div className="lp-list-tabs">
            {listes.map((liste, index) => (
              <div
                key={liste.id_liste}
                className={`lp-tab-row ${listeActive?.id_liste === liste.id_liste ? "active" : ""}`}
              >
                <button
                  type="button"
                  className="lp-tab"
                  onClick={() => setListeActiveId(liste.id_liste)}
                >
                  <span>{liste.titre_liste}</span>
                  <small>{liste.items?.filter((item) => !item.item_coche).length ?? 0}</small>
                </button>

                <div className="lp-sort-controls" aria-label={`Classer la liste ${liste.titre_liste}`}>
                  <button
                    type="button"
                    onClick={() => deplacerListe(liste.id_liste, "up")}
                    disabled={index === 0}
                    title="Monter cette liste"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => deplacerListe(liste.id_liste, "down")}
                    disabled={index === listes.length - 1}
                    title="Descendre cette liste"
                  >
                    ▼
                  </button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="lp-content">
          {!listeActive ? (
            <div className="lp-empty">Crée une première liste.</div>
          ) : (
            <>
              <div className="lp-title-row">
                <h3>{listeActive.titre_liste}</h3>
                <button className="lp-danger" onClick={() => supprimerListe(listeActive.id_liste)}>
                  Supprimer la liste
                </button>
              </div>

              <form className="lp-add-item" onSubmit={ajouterItem}>
                <input
                  value={nouvelItem}
                  onChange={(e) => setNouvelItem(e.target.value)}
                  placeholder="Ajouter une ligne"
                />
                <button type="submit">Ajouter</button>
              </form>

              <ul className="lp-items">
                {(listeActive.items || []).map((item) => (
                  <li key={item.id_item} className={item.item_coche ? "done" : ""}>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(item.item_coche)}
                        onChange={() => toggleItem(item)}
                      />
                      <span>{item.texte_item}</span>
                    </label>
                    <button className="lp-delete-item" onClick={() => supprimerItem(item.id_item)}>
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </main>
      </div>
    </section>
  );
}
